import { inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, map, of } from 'rxjs';

import { BranchEntry, BranchesSummary } from '../../interfaces/branches-summary';
import { AbstractPurchasingService } from '../../services/abstract-purchasing.service';

/**
 * Rótulo do local de entrega de um item: código da filial e apelido do endereço.
 *
 * "0104 · Matriz"; se o apelido já contém o código ("FILIAL 0104"), mostra só o apelido; com um
 * dos dois vazio, mostra o que houver. Vazio quando o endereço não tem nenhum dos dois.
 */
export function branchLabel(item: { branch?: string; branchName?: string }): string {
  const code = item.branch?.trim();
  const name = item.branchName?.trim();
  if (code && name && name.includes(code)) return name;
  return [code, name].filter(Boolean).join(' · ');
}

/** Endereço do local de entrega de um item, como vem do mapeamento da API. */
interface DeliveryBranchSource {
  branch?: string;
  branchName?: string;
  branchId?: string;
  branchStreet?: string;
  /** CEP, já com o prefixo ("CEP 28470-000"). */
  branchCity?: string;
}

/** "Cidade/UF" de cada endereço da empresa, pelo id do endereço. */
export type BranchCities = ReadonlyMap<string, string>;

/**
 * "Cidade/UF" dos endereços da empresa, por id. Os itens só trazem os ids da cidade e do estado;
 * a lista de endereços da empresa traz os nomes. Enquanto ela não chega (ou se falhar), o mapa
 * fica vazio e os locais aparecem só com rua e CEP. Chamar em contexto de injeção.
 */
export function injectBranchCities(): Signal<BranchCities> {
  const empty: BranchCities = new Map<string, string>();

  return toSignal(
    inject(AbstractPurchasingService)
      .getBranchAddresses()
      .pipe(
        map(enderecos => {
          const cities = new Map<string, string>();
          for (const endereco of enderecos) {
            const city = [endereco.cidadeObj?.nome, endereco.ufObj?.uf ?? endereco.cidadeObj?.uf]
              .filter(Boolean)
              .join('/');
            if (city) cities.set(String(endereco.id), city);
          }
          return cities as BranchCities;
        }),
        catchError(() => of(empty))
      ),
    { initialValue: empty }
  );
}

/** "Sorriso/MT · CEP 78890-000": cidade e estado (quando conhecidos) e o CEP. */
export function deliveryCityLine(item: DeliveryBranchSource, cities?: BranchCities): string | undefined {
  const city = item.branchId ? cities?.get(item.branchId) : undefined;
  return [city, item.branchCity].filter(Boolean).join(' · ') || undefined;
}

/**
 * Locais de entrega dos itens, sem repetir, no formato do chip de locais do topo das telas:
 * código da filial e apelido como nome, com rua, cidade/UF e CEP na dica.
 */
export function toDeliveryBranchesSummary(
  items: readonly DeliveryBranchSource[],
  cities?: BranchCities
): BranchesSummary {
  const branches = new Map<string, BranchEntry>();
  for (const item of items) {
    const name = branchLabel(item);
    if (!name || branches.has(name)) continue;
    branches.set(name, {
      id: name,
      name,
      street: item.branchStreet,
      city: deliveryCityLine(item, cities)
    });
  }
  return { branches: [...branches.values()] };
}

/** "1 local de entrega" / "3 locais de entrega". */
export function deliveryBranchesLabel(summary: BranchesSummary): string {
  const n = summary.branches.length;
  return `${n} ${n === 1 ? 'local de entrega' : 'locais de entrega'}`;
}
